import Block from '@components/Layout/Block';
import { getListingHighlightsImages } from '@data/listings';
import { M2_TO_SQ_FT } from '@util/constants';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import { getTestId } from '@util/getTestIds';
import { useTranslations } from 'next-intl';
import Image from 'next/legacy/image';
import { FC, useEffect, useState } from 'react';
import {
  ContentSpan,
  ContentSpanCommunity,
  Header,
  Listing,
  ListingHeader,
  ListingHeaderCommunity,
  ListingImageWrapper,
  ListingsContainer,
  ListingWithImage,
  NoContentSpanCommunity,
  Tab,
  TabContainer,
} from './styles';

// Tab Listing Types
interface TabListingProps {
  header?: string;
  content?: string;
}

enum AssociationFeeFrequencyTranslationKeyEnum {
  Annually = 'AssociationFeeFrequency.Annually',
  Quarterly = 'AssociationFeeFrequency.Quarterly',
  Monthly = 'AssociationFeeFrequency.Monthly',
  Weekly = 'AssociationFeeFrequency.Weekly',
  'Semi-Annually' = 'AssociationFeeFrequency.Semi-Annually',
  Daily = 'AssociationFeeFrequency.Daily',
  'Bi-Weekly' = 'AssociationFeeFrequency.Bi-Weekly',
  'Bi-Monthly' = 'AssociationFeeFrequency.Bi-Monthly',
  Seasonal = 'AssociationFeeFrequency.Seasonal',
  'One Time' = 'AssociationFeeFrequency.One Time',
  'Semi-Monthly' = 'AssociationFeeFrequency.Semi-Monthly',
}
interface CommunityTabListingProps extends TabListingProps {
  img?: string;
  imgs?: any;
}

interface SchoolTabListingProps extends TabListingProps {
  rating?: number;
  students?: string;
  distance?: string;
  reviewAvg?: number;
  reviewNum?: number;
}

// Tab Type
interface TabProps {
  header: string;
  tabs: {
    type?: 'standard' | 'community' | 'school';
    header?: string;
    tabListings: TabListingProps[] | CommunityTabListingProps[] | SchoolTabListingProps[];
  }[];
}

// TAB COMPONENTS

export const TabListing: FC<TabListingProps> = ({ header, content }) => {
  return header && content && content !== '-' ? (
    <Listing>
      <ListingHeader data-testid={`${getTestId(header)}-listing-header`}>{header}</ListingHeader>
      {/* // <ContentSpan>{t(content)}</ContentSpan> */}
      <ContentSpan data-testid={`${getTestId(header)}-listing-content`}>{content}</ContentSpan>
    </Listing>
  ) : (
    <></>
  );
};

const CommunityTabListing: FC<CommunityTabListingProps> = ({ header, content, img, imgs }) => {
  //get image url from imgs array based on img id
  const imgObj = imgs?.find((image) => image?.sys?.id === img);
  let imgIcon = imgObj?.fields?.file?.url;
  if (imgIcon && imgIcon.startsWith('//images.ctfassets.net/')) {
    imgIcon = 'https:' + imgIcon;
  }
  return header && content && content !== '-' ? (
    <ListingWithImage>
      <ListingImageWrapper>
        {imgIcon && (
          <Image
            src={imgIcon}
            width={`${imgObj?.fields?.file?.details?.image?.width}`}
            height={`${imgObj?.fields?.file?.details?.image?.height}`}
            alt={header}
            unoptimized={true}
            priority={true}
            layout="fill"
            sizes="100vw"
          />
        )}
      </ListingImageWrapper>
      <ListingHeaderCommunity>{header}</ListingHeaderCommunity>
      {content ? (
        <ContentSpanCommunity>{content}</ContentSpanCommunity>
      ) : (
        <NoContentSpanCommunity>-</NoContentSpanCommunity>
      )}
    </ListingWithImage>
  ) : (
    <></>
  );
};

interface ListingHighlightsProps {
  tabs: TabProps['tabs'];
  currencyCode: string;
}

// PARENT COMPONENT
const ListingHighlights: FC<ListingHighlightsProps> = ({ tabs, currencyCode }) => {
  const t = useTranslations();
  const { convertArea, preferredMeasurement } = useMeasurement();
  const { preferredCurrency, convertCurrency, formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState(0);
  const [modifiedTabs, setModifiedTabs] = useState(tabs);
  const [images, setImages] = useState([]);

  //look through tabs and collect all the image ids from community type tab, ignore duplicates
  const imageIds = tabs.reduce<string[]>((acc, tab) => {
    if (tab.type === 'community') {
      (tab.tabListings as CommunityTabListingProps[]).forEach((listing) => {
        if (listing.img) {
          acc.push(listing.img);
        }
      });
    }
    return acc;
  }, []);

  // get the images for the listing highlights
  const fetchImages = async () => {
    try {
      const imageObjs = await getListingHighlightsImages(imageIds);
      return setImages(imageObjs?.items);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (imageIds.length > 0) {
      // Check if imageIds are available
      fetchImages();
    }
  }, [tabs]);

  // Sets active tab
  const handleTabClick = (i: number): void => {
    setActiveTab(i);
  };

  useEffect(() => {
    let newTabs = structuredClone(tabs);
    const finalCurrency = preferredCurrency ?? currencyCode;
    tabs.map((item, i) => {
      item?.tabListings.map((tab, tabInx) => {
        const isSqM = preferredMeasurement === 'm²';
        const newTab = newTabs[i]!.tabListings[tabInx]!;
        const convertTabContentUnitMeasurments = (partialTabName: string) => {
          newTab.header = `${partialTabName} (Sq ${isSqM ? 'M' : 'Ft'})`;
          newTab.content = convertArea(
            Number(tab?.content?.toString().replace(',', '')),
            'sq.ft',
          ).formattedArea;
        };

        if (tab.content == '-' || !tab.content) return;
        switch (tab.header) {
          case t('Estimated Median Income'):
            newTab.content = formatCurrency(
              convertCurrency(tab.content, currencyCode),
              finalCurrency,
            );
            break;
          case t('Tax Annual Amount'):
            newTab.content = formatCurrency(
              convertCurrency(tab.content, currencyCode),
              finalCurrency,
            );
            break;
          case t('Association Fee'):
            newTab.content = `${(newTab.content = formatCurrency(
              convertCurrency(tab.content, currencyCode),
              finalCurrency,
            ))}/${t(AssociationFeeFrequencyTranslationKeyEnum[tab.additionalContent])}`;
            break;
          case t('ListingHighlights.PropertyTab.pricePer', {
            unit: isSqM ? 'M' : tab.additionalContent,
          }):
            newTab.header = t('ListingHighlights.PropertyTab.pricePer', {
              unit: isSqM ? 'M' : tab.additionalContent,
            });
            newTab.header.includes('Sq M')
              ? (newTab.content = formatCurrency(
                  convertCurrency(parseFloat(tab.content) * M2_TO_SQ_FT, currencyCode),
                  finalCurrency,
                ))
              : (newTab.content = formatCurrency(
                  convertCurrency(parseFloat(tab.content), currencyCode),
                  finalCurrency,
                ));
            break;
          case t('Living Area (Sq Ft)'):
            convertTabContentUnitMeasurments('Living Area');
            break;
          case t('Lot Size (Sq Ft)'):
            convertTabContentUnitMeasurments('Lot Size');
            break;
        }
      });
    });
    setModifiedTabs(newTabs);
  }, [tabs, preferredMeasurement, preferredCurrency, formatCurrency, convertCurrency]);

  return (
    <Block narrow>
      <Header data-testid={`listing-highlights-header`}>{t('ListingHighlights.title')}</Header>
      {modifiedTabs && (
        <>
          <TabContainer>
            {modifiedTabs.map((t, i) => {
              const header = t.header ?? '';
              const isTabContentEmpty =
                t.tabListings &&
                t.tabListings.length > 0 &&
                t.tabListings.every((t) => t.content === '-'); // API returning '-' for empty data
              if (!isTabContentEmpty) {
                return (
                  <Tab
                    key={i}
                    onClick={() => handleTabClick(i)}
                    $active={i === activeTab}
                    data-testid={`listing-highlights-${getTestId(header)}-tab`}
                  >
                    {header}
                  </Tab>
                );
              }
            })}
          </TabContainer>
          <ListingsContainer
            $smallGap={modifiedTabs[activeTab]!.type !== 'community'}
            data-testid={`${modifiedTabs[activeTab]!.header?.toLowerCase()}-listings-container`}
          >
            {modifiedTabs[activeTab]!.tabListings.map((l, lIndx) => {
              return (
                // Render Community tab
                modifiedTabs[activeTab]!.type === 'community' ? (
                  <CommunityTabListing
                    key={lIndx}
                    imgs={images}
                    {...l}
                    data-testid="community-tab-listing"
                  />
                ) : (
                  // Render Default tab
                  <TabListing key={lIndx} {...l} data-testid="default-tab-listing" />
                )
              );
            })}
          </ListingsContainer>
        </>
      )}
    </Block>
  );
};

export default ListingHighlights;
